/* eslint-disable react/jsx-props-no-spreading */
import { Button, Form, Input as AntInput, Modal, message } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'

import { Formik } from 'formik'
import mapValues from 'lodash/mapValues'
import PropTypes from 'prop-types'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../api'
import { formElementPropsGenerator } from '../../lib/forms'
import { Yup } from '../../lib/validations'
import { Input, UploadFormikFile } from '../Forms'
import TextArea from '../Forms/TextArea'
import Switch from '../Forms/Switch'
import Item from '../Forms/Item'
import { practiceShape } from '../prop-types'
import AccountManagerSelect from '../Forms/AccountManagerSelect'
import AlertsRecipients from './AlertsRecipients'
import IntegrationNameField from './IntegrationNameField'
import Phrases from './Phrases'
import Locations from './Locations'

const MAX_SIZE_LOGO = 300 * 1024

const PRACTICES_LABELS = {
  name: 'Name',
  address: 'Address',
  phoneNumber: 'Phone Number',
  faxNumber: 'Fax Number',
  logo: 'Logo',
  ownerEmails: 'Default alerts recipients',
  notificationsEmails: 'Default notifications recipients',
  insuranceRecipients: 'Insurance recipients',
  bpAlertRecipients: 'Blood Pressure recipients',
  accountManagerId: 'Account Manager',
  notifyPatientDeactivation: 'Notify Patient Deactivation',
  notifyPatientConnectivity: 'Notify Patient Connectivity',
  integrationName: 'Integration Name',
}

export const NON_FILE_PRACTICE_FIELDS = Object.keys(PRACTICES_LABELS).filter(
  field => field !== 'logo',
)

const PRACTICES_PLACEHOLDERS = {
  name: 'Doe Healthcare',
  address: 'Address',
  phoneNumber: 'Phone number',
  faxNumber: 'Fax number',
  ownerEmails: 'owner@gobiobridge.com',
  notificationsEmails: 'notifications@gobiobridge.com',
  insuranceRecipients: 'insurance@gobiobridge.com',
  bpAlertRecipients: 'alert@gobiobridge.com',
  accountManagerId: 'Account Manager',
  integrationName: 'integration_name',
}

const DEFAULT_VALUES = mapValues(PRACTICES_LABELS, '')
DEFAULT_VALUES.notifyPatientDeactivation = false
DEFAULT_VALUES.notifyPatientConnectivity = false

const labelCol = {
  sm: { span: 4, offset: 2 },
}

const wrapperCol = {
  sm: { span: 16 },
}

const actionsWrapperCol = {
  sm: { span: 16, offset: labelCol.sm.span + labelCol.sm.offset },
}

const propsFor = formElementPropsGenerator({
  formName: 'practice-form',
  labels: PRACTICES_LABELS,
  placeholders: PRACTICES_PLACEHOLDERS,
})

function deriveIntegrationName(name) {
  return (name || '')
    .replace(/\[[^\]]*\]/g, '') // strip bracketed suffixes e.g. [LLC]
    .replace(/\([^)]*\)/g, '') // strip parenthetical asides e.g. (main)
    .normalize('NFD') // decompose accented chars into base + combining marks
    .replace(/[̀-ͯ]/g, '') // drop the combining marks, leaving ASCII base
    .toLowerCase() // fold to lowercase before slug-safe char filter
    .replace(/[^a-z0-9]+/g, '_') // collapse non-alphanumeric runs into _
    .replace(/^_+|_+$/g, '') // trim leading/trailing underscores
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required(),
  address: Yup.string().required(),
  phoneNumber: Yup.string().required(),
  ownerEmails: Yup.string(),
  notificationsEmails: Yup.string(),
  insuranceRecipients: Yup.string(),
  bpAlertRecipients: Yup.string(),
  accountManagerId: Yup.string().nullable(),
  logo: Yup.mixed()
    .required('You need to provide a file')
    .test('fileSize', 'The file is too large', value => {
      if (typeof value === 'string') return true

      return value && value[0].size <= MAX_SIZE_LOGO
    }),
})

export default function PracticeForm({ practiceData = undefined, onSubmit }) {
  const navigate = useNavigate()
  const integrationNameLocked = Boolean(practiceData?.integrationName?.trim())
  const [integrationNameDirty, setIntegrationNameDirty] = useState(false)
  const browseProviders = useCallback(() => {
    navigate(`/practices/${practiceData.id}/providers`, { replace: true })
  }, [navigate, practiceData])

  const browseLocations = useCallback(() => {
    navigate(`/practices/${practiceData.id}/locations`, { replace: true })
  }, [navigate, practiceData])

  const browsePresetPhrases = useCallback(() => {
    navigate(`/practices/${practiceData.id}/preset-phrases`, { replace: true })
  }, [navigate, practiceData])

  const isActive = practiceData?.active !== false

  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [deactivating, setDeactivating] = useState(false)

  const handleDeactivate = useCallback(async () => {
    setDeactivating(true)
    try {
      await api.practices.deletePractice(practiceData.id)
      message.success('Practice successfully deactivated')
      navigate(-1)
    } catch (e) {
      message.error('Could not deactivate practice. Please try again.')
    } finally {
      setDeactivating(false)
    }
  }, [navigate, practiceData])

  const handleToggleActive = useCallback(() => {
    if (isActive) {
      setDeactivateModalOpen(true)
      return
    }

    Modal.confirm({
      title: 'Activate this practice?',
      icon: <ExclamationCircleOutlined />,
      content:
        'This practice will be reactivated and will appear in active lists again.',
      okText: 'Activate',
      async onOk() {
        try {
          await api.practices.updatePractice(practiceData.id, { active: true })
          message.success('Practice successfully activated')
          navigate(-1)
        } catch (e) {
          message.error('Could not activate practice. Please try again.')
        }
      },
    })
  }, [isActive, navigate, practiceData])

  return (
    <div>
      <Formik
        initialValues={practiceData || DEFAULT_VALUES}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({
          isSubmitting,
          handleSubmit,
          handleBlur,
          setFieldValue,
        }) => (
          <Form
            onFinish={handleSubmit}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
          >
            <Input
              {...propsFor('name')}
              onBlur={e => {
                handleBlur(e)
                if (!integrationNameLocked && !integrationNameDirty) {
                  setFieldValue(
                    'integrationName',
                    deriveIntegrationName(e.target.value),
                  )
                }
              }}
            />
            <Input {...propsFor('address')} />
            <Input {...propsFor('phoneNumber')} />
            <Input {...propsFor('faxNumber')} />
            <IntegrationNameField
              initialValue={practiceData?.integrationName}
              onUserEdit={value => setIntegrationNameDirty(Boolean(value))}
            />
            {practiceData?.medicalProviders && (
              <Item label="Alerts Recipients">
                <AlertsRecipients
                  providers={practiceData?.medicalProviders || [{}]}
                  bpRecipients={practiceData?.bpAlertRecipients || ''}
                  onManage={browseProviders}
                />
              </Item>
            )}

            <Item label="Office Locations">
              <Locations
                locations={practiceData?.officeLocations || [{}]}
                onManage={browseLocations}
              />
            </Item>

            <Item label="Preset Phrases">
              <Phrases
                phrases={practiceData?.practiceFormOptions || [{}]}
                onManage={browsePresetPhrases}
              />
            </Item>

            <TextArea {...propsFor('ownerEmails')} />
            <TextArea {...propsFor('notificationsEmails')} />
            <TextArea {...propsFor('insuranceRecipients')} />
            <UploadFormikFile
              name="logo"
              label={PRACTICES_LABELS.logo}
              supportFiles={['image/*']}
            />
            <div style={{ marginLeft: '25%' }}>
              <Switch
                {...propsFor('notifyPatientDeactivation')}
                label="Notify Patient Deactivation"
                leftAlign
                checkedChildren=" "
                unCheckedChildren=" "
                style={{ minWidth: 35, marginLeft: -35 }}
              />
              <Switch
                {...propsFor('notifyPatientConnectivity')}
                label="Notify Patient Connectivity"
                leftAlign
                checkedChildren=" "
                unCheckedChildren=" "
                style={{ minWidth: 35, marginLeft: -35 }}
              />
            </div>

            <AccountManagerSelect
              {...propsFor('accountManagerId')}
              label={PRACTICES_LABELS.accountManagerId}
              allowClear
              labelCol={labelCol}
              wrapperCol={wrapperCol}
              labelAlign="right"
            />

            <Form.Item wrapperCol={actionsWrapperCol}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  {practiceData ? 'Save Practice' : 'Create Practice'}
                </Button>
                {practiceData && (
                  <Button danger={isActive} onClick={handleToggleActive}>
                    {isActive ? 'Deactivate Practice' : 'Activate Practice'}
                  </Button>
                )}
              </div>
            </Form.Item>
          </Form>
        )}
      </Formik>

      <Modal
        title="Deactivate this practice?"
        open={deactivateModalOpen}
        onCancel={() => {
          setDeactivateModalOpen(false)
          setConfirmText('')
        }}
        onOk={handleDeactivate}
        okText="Deactivate"
        okType="danger"
        okButtonProps={{
          disabled: confirmText !== 'DEACTIVATE',
          loading: deactivating,
        }}
      >
        <p>
          Deactivating a practice deactivates all its patients. Are you sure?
          Type <strong>DEACTIVATE</strong> in the box below to continue.
        </p>
        <AntInput
          placeholder="Type DEACTIVATE to confirm"
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
        />
      </Modal>
    </div>
  )
}

PracticeForm.propTypes = {
  practiceData: practiceShape,
  onSubmit: PropTypes.func.isRequired,
}
